import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import toast from 'react-hot-toast';
import ChecksList from '../../components/agent/ChecksList';
import CompanyDocuments from '../../components/company/CompanyDocuments';

const CompanyDebtDetailsPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanyDetails();
  }, [companyId]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/field-agent/payments/company/${companyId}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching company details:', error);
      toast.error('שגיאה בטעינת נתוני החברה');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    fetchCompanyDetails();
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      // Check if iOS device
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
      
      if (isIOS) {
        // For iOS: Open PDF in new tab (Safari will display/download it)
        const baseUrl = axiosInstance.defaults.baseURL || '';
        const token = localStorage.getItem('token');
        const pdfUrl = `${baseUrl}/api/field-agent/payments/${paymentId}/receipt/pdf?token=${token}`;
        window.location.href = pdfUrl;
      } else {
        // For desktop/Android: Use blob download
        const response = await axiosInstance.get(`/api/field-agent/payments/${paymentId}/receipt/pdf`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `receipt-${paymentId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('שגיאה בהורדת הקבלה');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: 'מזומן',
      check: 'צ\'ק',
      bank_transfer: 'העברה בנקאית'
    };
    return labels[method] || method;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p>לא נמצאו נתונים</p>
      </div>
    );
  }

  const { company, credit_summary, payments } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" dir="rtl">
      {/* Back Button */}
      <button
        onClick={() => navigate('/agent/payments')}
        className="mb-4 text-primary-600 hover:text-primary-800 flex items-center gap-2"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        חזרה לרשימת החברות
      </button>

      {/* Company Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{company.name}</h1>
            <p className="text-gray-600">{company.email} | {company.phone}</p>
          </div>
          <button
            onClick={() => navigate(`/agent/payments/${companyId}/new`)}
            className="bg-primary-600 text-white px-6 py-3 rounded-md hover:bg-primary-700 font-medium"
          >
            רשום גבייה חדשה
          </button>
        </div>
      </div>

      {/* Credit Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">מסגרת אשראי</div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(credit_summary.credit_limit)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">יתרת חוב</div>
          <div className={`text-2xl font-bold ${credit_summary.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {formatCurrency(credit_summary.current_balance)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">סה"כ נגבה</div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(credit_summary.collected_amount || 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">סה"כ זיכויים</div>
          <div className="text-2xl font-bold text-orange-600">
            {formatCurrency(credit_summary.refunds_total || 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">אשראי זמין</div>
          <div className="text-2xl font-bold text-blue-600">
            {formatCurrency(credit_summary.available_credit)}
          </div>
        </div>
      </div>

      {/* Payments History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">היסטוריית תשלומים</h2>
        </div>

        {payments.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">אין תשלומים</h3>
            <p className="mt-1 text-sm text-gray-500">טרם נרשמו תשלומים לחברה זו</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                    קבלה
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                    תאריך
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                    סכום
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                    אמצעי
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 hidden sm:table-cell">
                    נגבה ע"י
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                    סטטוס
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 hidden md:table-cell">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <React.Fragment key={payment.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-2 py-3 text-xs font-medium text-gray-900">
                        <div className="max-w-[120px] truncate" title={payment.receipt_number}>
                          {payment.receipt_number}
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900">
                        {new Date(payment.payment_date).toLocaleDateString('he-IL', { 
                          day: '2-digit', 
                          month: '2-digit' 
                        })}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-xs font-bold text-green-600">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-2 py-3 text-xs text-gray-900">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1">
                          <span>{getPaymentMethodLabel(payment.payment_method)}</span>
                          {payment.checks?.length > 0 && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                              {payment.checks.length} צ'קים
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900 hidden sm:table-cell">
                        <div className="max-w-[100px] truncate" title={payment.collector?.name}>
                          {payment.collector?.name}
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap">
                        {payment.status === 'approved' && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                            אושר
                          </span>
                        )}
                        {payment.status === 'pending' && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">
                            ממתין
                          </span>
                        )}
                        {payment.status === 'rejected' && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                            נדחה
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-xs hidden md:table-cell">
                        <button
                          onClick={() => handleDownloadReceipt(payment.id)}
                          className="text-primary-600 hover:text-primary-800 font-medium text-xs"
                        >
                          הורד
                        </button>
                      </td>
                    </tr>
                    {payment.checks?.length > 0 && <ChecksList payment={payment} />}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Company Documents Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <CompanyDocuments 
          companyId={companyId} 
          canUpload={true} 
          canDelete={false}
        />
      </div>
    </div>
  );
};

export default CompanyDebtDetailsPage;

